import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { AuthLayout } from "@/components/catalyst/AuthLayout";
import { Button } from "@/components/catalyst/Button";
import {
	DescriptionDetails,
	DescriptionList,
	DescriptionTerm,
} from "@/components/catalyst/DescriptionList";
import { Heading, Subheading } from "@/components/catalyst/Heading";
import { Logo } from "@/components/Logo";
import { orpc } from "@/orpc/client";

type Status =
	| "idle"
	| "loading"
	| "review"
	| "approving"
	| "approved"
	| "error";

export function ReferralLinkApproval() {
	const [status, setStatus] = useState<Status>("idle");
	const [errorMsg, setErrorMsg] = useState("");

	const searchParams = useSearch({ from: "/partner" });
	const linkId = searchParams.linkId;
	const token = searchParams.token;

	const { data, isLoading, error } = useQuery(
		orpc.publicReferralLinkApproval.getDetails.queryOptions({
			input: linkId && token ? { linkId, token } : skipToken,
		}),
	);

	const approveMutation = useMutation(
		orpc.publicReferralLinkApproval.approve.mutationOptions({
			onSuccess: () => {
				setStatus("approved");
			},
			onError: (err) => {
				setStatus("error");
				setErrorMsg(err.message || "Failed to approve");
			},
		}),
	);

	useEffect(() => {
		if (!linkId || !token) {
			setStatus("error");
			setErrorMsg(
				"Invalid link. Please check your email for the correct link.",
			);
			return;
		}

		if (isLoading) {
			setStatus("loading");
		} else if (error) {
			setStatus("error");
			setErrorMsg(error.message || "Something went wrong");
		} else if (data?.alreadyApproved) {
			setStatus("approved");
		} else if (data) {
			setStatus("review");
		}
	}, [linkId, token, isLoading, error, data]);

	const handleApprove = () => {
		if (!linkId || !token) return;
		setStatus("approving");
		approveMutation.mutate({ linkId, token });
	};

	function renderContent() {
		if (status === "idle" || status === "loading") {
			return (
				<>
					<Heading>Loading...</Heading>
					<Subheading>Fetching referral link details</Subheading>
					<Loader2 className="block h-8 w-8 animate-spin text-base-content" />
				</>
			);
		}

		if (status === "error") {
			return (
				<>
					<div className="mx-auto w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mb-4">
						<XCircle className="size-6 text-error" />
					</div>
					<Heading>Something went wrong</Heading>
					<Subheading className="text-error">{errorMsg}</Subheading>
				</>
			);
		}

		if (status === "approved") {
			return (
				<div className="max-w-sm">
					<div className="mx-auto w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mb-4">
						<CheckCircle className="size-6 text-success" />
					</div>
					<Heading>Terms Approved</Heading>
					<Subheading>
						You have approved the referral link terms for {data?.link.appName}.
						The link is now active and can be used for attributions.
					</Subheading>
				</div>
			);
		}

		if (status === "review" || status === "approving") {
			const link = data?.link;
			const payoutPercent = link ? (link.payoutRateBps / 100).toFixed(2) : "0";
			const duration = link?.payoutDurationDays
				? `${link.payoutDurationDays} days`
				: "Lifetime";
			const expiry = link?.expiresAt
				? new Date(link.expiresAt).toLocaleDateString()
				: "Never";
			const minPayout = link?.minPayoutCents
				? `$${(link.minPayoutCents / 100).toFixed(2)}`
				: "None";

			return (
				<>
					<Heading>Review Referral Terms</Heading>
					<Subheading>
						Please review the terms for your referral link with {link?.appName}
					</Subheading>

					<DescriptionList className="mt-6 w-full max-w-md">
						<DescriptionTerm>App</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{link?.appName}
						</DescriptionDetails>

						<DescriptionTerm>Payout Rate</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{payoutPercent}% of revenue
						</DescriptionDetails>

						<DescriptionTerm>Payout Duration</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{duration}
						</DescriptionDetails>

						<DescriptionTerm>Link Expires</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{expiry}
						</DescriptionDetails>

						<DescriptionTerm>Minimum Payout</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{minPayout}
						</DescriptionDetails>
					</DescriptionList>

					<div className="mt-6 w-full max-w-md">
						<Button
							className="w-full"
							onClick={handleApprove}
							disabled={status === "approving"}
						>
							{status === "approving" ? (
								<>
									<Loader2 className="size-4 animate-spin mr-2" />
									Approving...
								</>
							) : (
								"Approve Terms"
							)}
						</Button>
						<p className="mt-3 text-sm text-base-content/60 text-center">
							By approving, you agree to receive payouts based on these terms.
						</p>
					</div>
				</>
			);
		}

		return null;
	}

	return (
		<AuthLayout>
			<div className="flex flex-col gap-6 w-full max-w-2xl">
				<div className="flex flex-col items-center gap-4">
					<Logo />
					{renderContent()}
				</div>
			</div>
		</AuthLayout>
	);
}
